import { useSelector } from 'react-redux';
import { useLocation, Outlet } from 'react-router-dom';
import React from 'react';
//  Internals
import { selectAdministrationSubMenu, selectIsLoading, selectModules } from 'app/components/modules/sideBar/slice/selectors';
import { getIsCDS, getIsClient, getIsFinance, getRoleName } from '../modules/sideBar/slice/selectors';
import { NoAccess } from '../noAccess/NoAccess';
import roles from '../../../data/roles';
import URL from '../../../data/url';
import LoadingSpinner from '../navigation/LoadingSpinner';

/**
 * ProtectedRoute component that checks user permissions before rendering child routes.
 * Uses React Router's Outlet for nested route rendering.
 * Best practice pattern as recommended by React Router v6 documentation.
 */
export const ProtectedRoute = () => {
  const location = useLocation();
  //  isCDS value
  const isCDS = useSelector(getIsCDS);
  //  isClient value
  const isClient = useSelector(getIsClient);
  //  isFinance value (finance team - gates the credit note page)
  const isFinance = useSelector(getIsFinance);
  //  role name (used to gate CDS-Admin-only pages)
  const roleName = useSelector(getRoleName);
  //  Main Menu
  const modules = useSelector(selectModules);
  const loading = useSelector(selectIsLoading);

  const modulePaths = modules.map(module => module.url.toLowerCase());
  //  Administration Menu
  const administrationSubmenu = useSelector(selectAdministrationSubMenu);
  const administrationSubmenuPaths = administrationSubmenu?.map(module => '/administration'.concat(module.url.toLowerCase()));
  const pathList = [...modulePaths, ...administrationSubmenuPaths];

  // Always allow root path for any authenticated user
  pathList.push('/');

  if (isCDS || isClient) pathList.push(URL.REQUEST_FOR_QUOTE);
  if (isCDS) pathList.push(URL.RFQ_SEARCH);
  if (isCDS) pathList.push(URL.PO_SEARCH);
  // Credit notes are a finance-team action, matching the API's RequireFinanceTeam policy.
  if (isFinance) pathList.push(URL.RAISE_CREDIT_NOTE);
  // Product catalogue administration is CDS Admin only (backend enforces RequireAdministrator).
  if (roleName === roles.CDS_NAME) pathList.push('/administration/productcatalogue');

  if (loading) {
    return (
      <div style={{ position: 'relative', top: '40vh' }}>
        <LoadingSpinner />
      </div>
    );
  }

  // Check if current path matches any allowed paths (supports dynamic routes)
  const hasAccess = pathList.some(allowedPath => {
    // Direct match for exact paths
    if (location.pathname.toLowerCase() === allowedPath) return true;
    // Check if it's a base path match (e.g., /administration/account for /administration/account/:accountid)
    if (location.pathname.toLowerCase().startsWith(allowedPath + '/')) return true;
    return false;
  });

  return hasAccess ? <Outlet /> : <NoAccess />;
};
